import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  Inject,
  OnDestroy,
  AfterViewInit
} from '@angular/core';
import {
  MatCalendar,
  MatCalendarView,
  MatDatepicker
} from '@angular/material/datepicker';
import {DateAdapter, MAT_DATE_FORMATS, MatDateFormats} from '@angular/material/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {
  DATEPICKER_PRESET_TODAY,
  DATEPICKER_PRESET_CUSTOM,
  DATEPICKER_PRESET
} from '../datepicker.constants';
import moment from 'moment';
import {UtilService} from '../../../services/util/util.service';

@Component({
  selector: 'app-datepicker-custom-header',
  styleUrls: ['../datepicker-header.component.scss'],
  templateUrl: './datepicker-customHeader.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DatepickerCustomHeaderComponent<D> implements AfterViewInit, OnDestroy {
  private _destroyed = new Subject<void>();
  readonly todayPreset = DATEPICKER_PRESET_TODAY;
  currentPreset: DATEPICKER_PRESET;
  noPresets: boolean;

  @HostBinding('class.touch-ui')
  readonly isTouchUi = this.picker.touchUi;

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    private picker: MatDatepicker<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    private cdr: ChangeDetectorRef,
    public utilService: UtilService
  ) {
    this.noPresets = (this.picker.panelClass[0] === 'calendar-no-preset');
    _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => {
      cdr.markForCheck();
      cdr.detectChanges();
    });
    _calendar._userSelection.pipe(takeUntil(this._destroyed)).subscribe(() => {
      const date: D = _calendar.selected as D;
      //this.picker.select(date);
      this.picker._applyPendingSelection();
      cdr.markForCheck();
      cdr.detectChanges();
      if (!this.noPresets) {
        this.picker.panelClass = DATEPICKER_PRESET_CUSTOM;
      }
    });
  }

  ngAfterViewInit() {
    this.setCalendars();
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  get getPanelClass(): string {
    return this.picker.panelClass[0];
  }

  periodMonthLabel() {
    return moment(this._calendar.activeDate).format('MMMM');
  }
  periodYearLabel() {
    return moment(this._calendar.activeDate).format('YYYY');
  }

  previousClicked() {
    this.setCalendars(-1);
  }

  nextClicked() {
    this.setCalendars(1);
  }

  private calculateDate(preset: DATEPICKER_PRESET): [date: D, preset: DATEPICKER_PRESET] {
    switch (preset) {
      case DATEPICKER_PRESET_TODAY: {
        return [this.today, preset];
      }
      default:
        return [this.today, DATEPICKER_PRESET_TODAY];
    }
  }

  private get today(): D {
    const now = this._dateAdapter.today();
    return this._dateAdapter.createDate(this._dateAdapter.getYear(now), this._dateAdapter.getMonth(now), this._dateAdapter.getDate(now));
  }

  selectDate(rangeName: DATEPICKER_PRESET): void {
    if (rangeName) {
      const [date, preset] = this.calculateDate(rangeName);
      this.applyDate(date, preset);
      /* temporary: applying one more time to avoid previous user selection */
      this.applyDate(date, preset);
    }
  }

  applyDate(date: D, preset: DATEPICKER_PRESET): void {
    this._calendar.selected = date;
    this._calendar.activeDate = date;
    this.setCalendars();
    this.picker.select(date);
    this._calendar._userSelection.emit({value: date, event: null});
    this.picker._applyPendingSelection();
    if (!this.noPresets) {
      this.picker.panelClass = preset;
    }
  }

  switchCalendarView(view: MatCalendarView) {
    if (this._calendar.currentView !== view) {
      this._calendar.currentView = view;
    } else {
      this._calendar.currentView = 'month';
    }
  }

  setCalendars(inMonths: number = 0): void {
    this._calendar.activeDate = this._dateAdapter.addCalendarMonths(this._calendar.activeDate, inMonths);
    this._calendar.currentView = 'month';
  }
}
